import { getApiConfig, isMockMode } from '@src/config/api';
import { clearPdfiumCache } from '@src/modules/pdfium_wrapper';
import store from '@src/store/index';
import {
  selectPdfAccessToken,
  selectRirekishoPdfAccessToken,
  updatePdfAccessToken,
  updateRirekishoPdfAccessUrl,
} from '@src/store/slices/unified/pdf_access_url';
import { useEffect, useMemo, useRef } from 'react';
import { useSelector } from 'react-redux';

/**
 * PDF状態管理とURL生成を行うカスタムフック
 *
 * @param isRirekishoMode - 履歴書モードかどうか
 * @returns トークン、PDF URL、初期状態フラグ
 */
export const usePdfState = (isRirekishoMode: boolean) => {
  const resumeToken = useSelector(selectPdfAccessToken);
  const rirekishoToken = useSelector(selectRirekishoPdfAccessToken);
  const token = isRirekishoMode ? rirekishoToken : resumeToken;
  const prevTokenRef = useRef<string | null>(null);

  /**
   * トークンが変わったときに古いBlob URLをクリア
   */
  useEffect(() => {
    const prevToken = prevTokenRef.current;
    const isMockToken = prevToken === 'mock-pdf-token' || prevToken === 'rirekisho_example.pdf';

    if (prevToken && prevToken !== token && !isMockToken) {
      clearPdfiumCache(prevToken);
    }
    prevTokenRef.current = token;
  }, [token]);

  /**
   * モックモード時の初期トークン設定
   */
  useEffect(() => {
    if (isMockMode() && (!token || token === '')) {
      if (isRirekishoMode) {
        store.dispatch(updateRirekishoPdfAccessUrl('rirekisho_example.pdf'));
      } else {
        store.dispatch(updatePdfAccessToken('mock-pdf-token'));
      }
    }
  }, [token, isRirekishoMode]);

  /**
   * メモ化されたPDF URL
   */
  const pdfUrl = useMemo((): string => {
    const mockMode = isMockMode();
    if (mockMode) {
      return isRirekishoMode
        ? `${window.location.origin}/rirekisho_example.pdf`
        : `${window.location.origin}/resume_example.pdf`;
    }

    if (!token) {
      return '';
    }

    return isRirekishoMode
      ? `${getApiConfig().baseUrl}/api/v1/pdf/rirekisho/${token}`
      : `${getApiConfig().baseUrl}/api/v1/pdf/resume/${token}`;
  }, [token, isRirekishoMode]);

  /**
   * 初期表示状態の判定
   */
  const isInitialState = useMemo((): boolean => {
    const mockMode = isMockMode();
    const initialState = mockMode ? false : !token || token === '';
    return initialState;
  }, [token]);

  return {
    token,
    pdfUrl,
    isInitialState,
  };
};
